/**
 * Runtime Live T1 funnel over persisted evaluation runs — not the definition Flow.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type {
  ProtocolVersion,
  T1AtomEvaluation,
  T1EvaluationRun,
  T1FunnelSnapshot,
  T1PatientEvaluation,
} from "../../api/clinicalTrialsApiTypes";
import {
  buildT1FunnelStages,
  dispositionForStage,
  funnelReconciles,
  type T1FunnelStageId,
} from "./protocolFunnelModel";

type ProtocolT1LiveFunnelProps = {
  trialId: string;
  versions: ProtocolVersion[];
};

/**
 * Render a truthful T1 funnel with patient drilldown for one trial.
 *
 * @param props - Trial id and published versions
 * @returns Live T1 funnel element
 */
export function ProtocolT1LiveFunnel({
  trialId,
  versions,
}: ProtocolT1LiveFunnelProps) {
  const [runs, setRuns] = useState<T1EvaluationRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [funnel, setFunnel] = useState<T1FunnelSnapshot | null>(null);
  const [patients, setPatients] = useState<T1PatientEvaluation[]>([]);
  const [selectedStage, setSelectedStage] = useState<T1FunnelStageId>("candidate");
  const [selectedMrn, setSelectedMrn] = useState<string | null>(null);
  const [atoms, setAtoms] = useState<T1AtomEvaluation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const stages = useMemo(
    () => (funnel ? buildT1FunnelStages(funnel) : []),
    [funnel],
  );

  const loadRuns = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await clinicalTrialsService.listT1Runs(trialId);
      setRuns(response.runs);
      if (!selectedRunId && response.runs[0]) {
        setSelectedRunId(response.runs[0].run_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load T1 runs");
    } finally {
      setBusy(false);
    }
  }, [trialId, selectedRunId]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (!selectedRunId) {
      setFunnel(null);
      setPatients([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setBusy(true);
      setError(null);
      try {
        const funnelResponse = await clinicalTrialsService.getT1Funnel(
          trialId,
          selectedRunId,
        );
        if (cancelled) return;
        setFunnel(funnelResponse.funnel);
        const disposition = dispositionForStage(selectedStage);
        const patientResponse = await clinicalTrialsService.listT1Patients(
          trialId,
          selectedRunId,
          disposition ? { disposition } : undefined,
        );
        if (cancelled) return;
        setPatients(patientResponse.patients);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load funnel");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [trialId, selectedRunId, selectedStage]);

  const openTrace = async (mrn: string) => {
    if (!selectedRunId) return;
    setSelectedMrn(mrn);
    setBusy(true);
    try {
      const trace = await clinicalTrialsService.getT1PatientTrace(
        trialId,
        selectedRunId,
        mrn,
      );
      setAtoms(trace.atoms);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load patient trace");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Live T1 funnel</h2>
          <p className="mt-1 max-w-3xl text-xs text-slate-600">
            Persisted shadow-mode T1 results only. Labels are passed / dropped /
            unresolved — never final eligibility. Definition Flow remains a
            separate blueprint.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          {versions.length} published version{versions.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-600" htmlFor="t1-run-select">
          Run
        </label>
        <select
          id="t1-run-select"
          className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          value={selectedRunId}
          onChange={(event) => {
            setSelectedRunId(event.target.value);
            setSelectedMrn(null);
            setAtoms([]);
          }}
        >
          <option value="">Select a T1 run</option>
          {runs.map((run) => (
            <option key={run.run_id} value={run.run_id}>
              {run.run_id} · {run.status} · {run.total} patients
            </option>
          ))}
        </select>
        <button
          className="rounded border border-slate-300 px-3 py-1.5 text-sm"
          disabled={busy}
          onClick={() => void loadRuns()}
        >
          Refresh
        </button>
      </div>

      {error ? (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {!selectedRunId ? (
        <div className="rounded border border-dashed border-slate-300 p-6 text-sm text-slate-500">
          No run selected. Create a shadow T1 run via the API after importing a
          READY execution binding for a published version.
        </div>
      ) : null}

      {funnel ? (
        <>
          <div className="text-xs text-slate-500">
            Accounting{" "}
            {funnelReconciles(funnel) ? "reconciles" : "does not reconcile"} ·
            candidates {funnel.candidate_count}
          </div>
          <div className="flex flex-wrap gap-2">
            {stages.map((stage) => (
              <button
                key={stage.stageId}
                className={`min-w-[140px] rounded-xl border px-3 py-3 text-left ${
                  selectedStage === stage.stageId
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-slate-50"
                }`}
                onClick={() => {
                  setSelectedStage(stage.stageId);
                  setSelectedMrn(null);
                  setAtoms([]);
                }}
              >
                <div className="text-lg font-semibold">{stage.count}</div>
                <div className="text-xs font-medium">{stage.label}</div>
                <div
                  className={`mt-1 text-[11px] ${
                    selectedStage === stage.stageId
                      ? "text-slate-200"
                      : "text-slate-500"
                  }`}
                >
                  {stage.description}
                </div>
              </button>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded border border-slate-200">
              <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Patients ({patients.length})
              </div>
              <ul className="max-h-80 overflow-auto divide-y divide-slate-100">
                {patients.map((patient) => (
                  <li key={patient.result_id}>
                    <button
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => void openTrace(patient.mrn)}
                    >
                      <span className="font-medium text-slate-800">
                        {patient.mrn}
                      </span>
                      <span className="text-xs text-slate-500">
                        {patient.disposition}
                        {patient.first_dropout_node_id
                          ? ` · ${patient.first_dropout_node_id}`
                          : ""}
                      </span>
                    </button>
                  </li>
                ))}
                {patients.length === 0 ? (
                  <li className="px-3 py-4 text-sm text-slate-500">
                    No patients in this stage.
                  </li>
                ) : null}
              </ul>
            </div>

            <div className="rounded border border-slate-200">
              <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Atom trace {selectedMrn ? `· ${selectedMrn}` : ""}
              </div>
              <ul className="max-h-80 overflow-auto divide-y divide-slate-100">
                {atoms.map((atom) => (
                  <li key={atom.atom_result_id} className="px-3 py-2 text-sm">
                    <div className="font-medium text-slate-800">
                      {atom.atom_id} · {atom.outcome}
                    </div>
                    <div className="text-xs text-slate-500">
                      {atom.criterion_id}
                      {atom.hard_stop_triggered ? " · hard-stop" : ""}
                      {atom.rescued ? " · rescued" : ""}
                    </div>
                    {atom.error_message ? (
                      <div className="text-xs text-rose-600">
                        {atom.error_message}
                      </div>
                    ) : null}
                  </li>
                ))}
                {!selectedMrn ? (
                  <li className="px-3 py-4 text-sm text-slate-500">
                    Select a patient to inspect atom-level evidence.
                  </li>
                ) : null}
              </ul>
            </div>
          </div>

          {Object.keys(funnel.dropout_reason_counts).length > 0 ? (
            <div className="rounded border border-slate-200 px-3 py-2 text-xs text-slate-600">
              Dropout reasons:{" "}
              {Object.entries(funnel.dropout_reason_counts)
                .map(([nodeId, count]) => `${nodeId}=${count}`)
                .join(", ")}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
